import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isJsonPrimitive(v: unknown): v is JsonPrimitive {
  return v === null || ["string", "number", "boolean"].includes(typeof v);
}

function formatPrimitive(v: JsonPrimitive): string {
  if (v === null) return "null";
  if (typeof v === "string") return JSON.stringify(v);
  return String(v);
}

function typeSummary(v: unknown): string {
  if (Array.isArray(v)) return `Array(${v.length})`;
  if (isRecord(v)) return `Object(${Object.keys(v).length})`;
  if (isJsonPrimitive(v)) return formatPrimitive(v);
  if (typeof v === "undefined") return "undefined";
  return String(v);
}

export interface JsonTreeProps {
  value: unknown;
  maxHeight?: number;
  defaultExpandedDepth?: number;
}

function JsonNode({
  name,
  value,
  depth,
  defaultExpandedDepth,
}: {
  name: string | null;
  value: unknown;
  depth: number;
  defaultExpandedDepth: number;
}) {
  const isExpandable = Array.isArray(value) || isRecord(value);
  const [open, setOpen] = useState(depth < defaultExpandedDepth);

  const children = useMemo(() => {
    if (!isExpandable) return [];

    if (Array.isArray(value)) {
      return value.map((v, idx) => ({ key: String(idx), name: `[${idx}]`, value: v }));
    }

    const entries = Object.entries(value as Record<string, unknown>);
    entries.sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([k, v]) => ({ key: k, name: k, value: v }));
  }, [isExpandable, value]);

  return (
    <Box sx={{ pl: depth === 0 ? 0 : 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {isExpandable ? (
          <IconButton
            size="small"
            onClick={() => setOpen((v) => !v)}
            sx={{ width: 24, height: 24, flex: "0 0 auto" }}
            aria-label={open ? "Collapse" : "Expand"}
          >
            {open ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, height: 24, flex: "0 0 auto" }} />
        )}

        {name != null ? (
          <Typography
            variant="body2"
            className="om-text-mono"
            sx={{ fontWeight: 600, opacity: 0.9, flex: "0 0 auto" }}
          >
            {name}
          </Typography>
        ) : null}

        <Typography
          variant="body2"
          className="om-text-mono"
          sx={{
            opacity: name != null ? 0.7 : 0.9,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name != null ? ":" : ""} {typeSummary(value)}
        </Typography>
      </Stack>

      {isExpandable && open ? (
        <Box sx={{ mt: 0.25 }}>
          {children.length === 0 ? (
            <Typography variant="caption" sx={{ opacity: 0.7, pl: 5 }}>
              (empty)
            </Typography>
          ) : (
            children.map((c) => (
              <JsonNode
                key={`${depth}-${c.key}`}
                name={c.name}
                value={c.value}
                depth={depth + 1}
                defaultExpandedDepth={defaultExpandedDepth}
              />
            ))
          )}
        </Box>
      ) : null}
    </Box>
  );
}

export function JsonTree({ value, maxHeight = 520, defaultExpandedDepth = 2 }: JsonTreeProps) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        overflow: "auto",
        maxHeight,
      }}
    >
      <JsonNode name={null} value={value} depth={0} defaultExpandedDepth={defaultExpandedDepth} />
    </Box>
  );
}
